import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bot, Lock, MessageSquare, Phone, Sparkles } from "lucide-react";
import { getMyProfile, getMyBookings } from "@/lib/api/portal.functions";
import { site } from "@/lib/site";

export const Route = createFileRoute("/_authenticated/portal/chat")({
  component: ChatPage,
});

function ChatPage() {
  const fetchProfile = useServerFn(getMyProfile);
  const fetchBookings = useServerFn(getMyBookings);
  const { data: profile } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
  });
  const { data: bookingsData } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => fetchBookings(),
  });

  const name = profile?.profile?.full_name || profile?.profile?.email || "";
  const intro = `Hi, this is ${name || "a portal client"}. I'd like to chat about my matter.`;
  const wa = `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(intro)}`;

  const bookings = bookingsData?.bookings ?? [];
  const hasPaidAccess = bookings.some(
    (b) => b.status === "confirmed" || b.status === "completed",
  );

  return (
    <section className="space-y-6">
      <div>
        <h2 className="mb-2 font-display text-xl text-navy">Get help</h2>
        <p className="text-sm text-muted-foreground">
          Need quick answers? Our AI Legal Assistant is available 24/7 for general
          information. For one-on-one live chat with our consultant, a paid
          consultation is required.
        </p>
      </div>

      {/* AI Assistant - always free */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy/10">
            <Bot className="h-5 w-5 text-navy" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg text-navy">AI Legal Assistant</h3>
              <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-medium text-navy">
                <Sparkles className="h-3 w-3" /> Free · Instant
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask about services, procedure, CCMA timelines, or how to prepare for
              a consultation. Answers are general information, not legal advice.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              Tap the <strong>AI Legal Assistant</strong> button at the
              bottom-right of any page to start chatting.
            </p>
          </div>
        </div>
      </div>

      {/* Live human chat - paid */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/20">
            {hasPaidAccess ? (
              <MessageSquare className="h-5 w-5 text-navy" />
            ) : (
              <Lock className="h-5 w-5 text-navy" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg text-navy">
              Live chat with our consultant
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Direct WhatsApp and phone access to Nkadimeng Danny for your matter.
              Available once you have a confirmed paid consultation booking on
              file.
            </p>

            {hasPaidAccess ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <a
                  href={wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg bg-[#25D366] p-4 text-white shadow-soft hover:scale-[1.01] transition-transform"
                >
                  <MessageSquare className="h-5 w-5" />
                  <div>
                    <p className="text-sm font-semibold">Chat on WhatsApp</p>
                    <p className="text-[11px] opacity-90">{site.phone}</p>
                  </div>
                </a>
                <a
                  href={`tel:${site.phoneIntl}`}
                  className="flex items-center gap-3 rounded-lg border border-border bg-background p-4 hover:border-navy"
                >
                  <Phone className="h-5 w-5 text-navy" />
                  <div>
                    <p className="text-sm font-semibold text-navy">Call</p>
                    <p className="text-[11px] text-muted-foreground">
                      {site.phone}
                    </p>
                  </div>
                </a>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-dashed border-gold/50 bg-gold/5 p-4">
                <p className="text-sm text-foreground">
                  You don't have a confirmed consultation yet. Book a paid
                  consultation to unlock live chat and call access.
                </p>
                <Link
                  to="/book"
                  className="mt-3 inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90"
                >
                  Book a consultation
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
